/**
 * Job Hunter — the two WhatsApp templates the agents use when the 24-hour
 * window is closed: one message per match (highlights + Apply / Skip /
 * Details buttons) and a daily digest. Created on the candidate's number in
 * one click, then reviewed by Meta.
 */
import { ReactNode, useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { CircleCheck, Clock3, CornerUpLeft, LoaderCircle, MessageSquareDashed, RefreshCw, Sparkles, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/Button';
import { jobHunterService, type JhNotifyOptions, type JhTemplateDefinition, type JhTemplateState, type JhTemplateStatus } from '@/services/job-hunter.service';
import { Pill, JhSelect } from '../ui';

function fill(text: string, samples: Record<string, string> = {}) {
  return text.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_m, k: string) => samples[k] ?? `{{${k}}}`);
}
/** WhatsApp *bold* → <b>. */
function rich(text: string): ReactNode[] {
  return text.split(/(\*[^*\n]+\*)/g).map((part, i) => (/^\*[^*]+\*$/.test(part) ? <b key={i}>{part.slice(1, -1)}</b> : part));
}

export function WhatsAppPreview({ def }: { def: JhTemplateDefinition }) {
  const s = def.previewSamples ?? {};
  return (
    <div className="jh-wa">
      <div className="jh-wa-bubble">
        <div className="px-3 pb-2 pt-2.5">
          {def.headerType === 'TEXT' && def.headerText && <div className="mb-1 text-[13.5px] font-bold">{fill(def.headerText, s)}</div>}
          <div className="whitespace-pre-wrap">{rich(fill(def.body, s))}</div>
          {def.footer && <div className="mt-1.5 text-[11.5px] text-[#667781]">{def.footer}</div>}
          <div className="mt-0.5 text-right text-[10.5px] text-[#667781]">09:41</div>
        </div>
        {(def.buttons ?? []).map((b) => <div key={b.text} className="jh-wa-btn"><CornerUpLeft size={13} />{b.text}</div>)}
      </div>
    </div>
  );
}

function StatusPill({ t }: { t: JhTemplateState | null }) {
  if (!t) return <Pill tone="neutral" icon={<MessageSquareDashed size={11} />}>Not created</Pill>;
  if (t.status === 'APPROVED') return <Pill tone="success" icon={<CircleCheck size={11} />}>Approved{t.linked ? '' : ' · not linked'}</Pill>;
  if (t.status === 'PENDING') return <Pill tone="warning" icon={<Clock3 size={11} />}>In review by Meta</Pill>;
  if (t.status === 'REJECTED') return <Pill tone="danger" icon={<TriangleAlert size={11} />}>Rejected</Pill>;
  return <Pill tone="neutral">{t.status.charAt(0) + t.status.slice(1).toLowerCase()}</Pill>;
}

export function TemplatesCard({ profileUid, opts, onLinked }: { profileUid: string; opts: JhNotifyOptions | null; onLinked: () => void }) {
  const [st, setSt] = useState<JhTemplateStatus | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [accountUid, setAccountUid] = useState('');

  const load = useCallback(async () => { try { setSt(await jobHunterService.templates(profileUid)); } catch { /* toasted */ } }, [profileUid]);
  useEffect(() => { setSt(null); void load(); }, [load]);
  useEffect(() => {
    if (accountUid) return;
    const uid = st?.account?.accountUid ?? opts?.accounts.find((a) => a.isActive)?.accountUid ?? '';
    if (uid) setAccountUid(uid);
  }, [st?.account, opts?.accounts, accountUid]);

  const create = async () => {
    setBusy('create');
    try {
      const r = await jobHunterService.createTemplates(profileUid, accountUid || undefined);
      setSt(r.status);
      const failed = Object.entries(r.results).filter(([, v]) => v.startsWith('failed'));
      if (failed.length) toast.error(failed.map(([k, v]) => `${k}: ${v.replace(/^failed: /, '')}`).join(' · '), { duration: 9000 });
      else toast.success('Templates sent to Meta for review — alerts use them as soon as they are approved');
      onLinked();
    } catch { /* toasted */ } finally { setBusy(null); }
  };
  const sync = async () => {
    setBusy('sync');
    try { setSt(await jobHunterService.syncTemplates(profileUid)); toast.success('Status refreshed from Meta'); } catch { /* toasted */ } finally { setBusy(null); }
  };

  const both = st?.alert && st?.digest;
  const allApproved = st?.alert?.status === 'APPROVED' && st?.digest?.status === 'APPROVED';
  const accounts = opts?.accounts ?? [];

  return (
    <section className="jh-card jh-card-pad">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="jh-card-title"><Sparkles size={16} className="text-primary" />Message templates</div>
          <div className="jh-card-sub max-w-2xl">
            WhatsApp only lets a business message you first with an approved template. Each strong match comes as <b>one message</b> — the highlights plus <b>Apply / Skip / Show details</b> buttons — so you can approve from the notification itself. A second “approve?” message would cost twice and split the decision from the facts. The digest lists the rest once a day.
          </div>
        </div>
        {st && (both ? <Button size="sm" variant="secondary" loading={busy === 'sync'} iconLeft={<RefreshCw size={13} />} onClick={sync}>Refresh status</Button> : null)}
      </div>

      {!st ? <div className="mt-4 grid h-24 place-items-center"><LoaderCircle className="animate-spin text-primary" size={20} /></div> : (
        <div className="mt-4 grid gap-5 @4xl:grid-cols-[minmax(0,1fr)_minmax(0,380px)]">
          <div className="space-y-3">
            {([['alert', 'Match alert', 'job_match_alert', 'One per strong match, with the decision buttons.'], ['digest', 'Daily digest', 'job_matches_digest', '“N new matches” with a Show jobs button.']] as const).map(([key, title, name, hint]) => {
              const t = st[key];
              return (
                <div key={key} className="rounded-xl border border-border p-3.5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0"><div className="text-[13px] font-semibold">{title}</div><div className="font-mono text-[11px] text-text-muted">{t?.name ?? name}</div></div>
                    <StatusPill t={t} />
                  </div>
                  <div className="jh-field-hint">{hint}</div>
                  {t?.status === 'REJECTED' && t.rejectionReason && <div className="mt-2 rounded-lg bg-danger-soft px-2.5 py-1.5 text-[12px] text-danger">Meta: {t.rejectionReason}</div>}
                </div>
              );
            })}

            {!allApproved && (
              accounts.length === 0 && !st.account ? (
                <div className="rounded-xl border border-warning/40 bg-warning-soft/40 px-3.5 py-3 text-[12.5px]">Connect a WhatsApp number under WhatsApp → Accounts first — templates belong to a number.</div>
              ) : (
                <div className="flex flex-wrap items-end gap-2">
                  {accounts.length > 1 && (
                    <label className="min-w-0 flex-1">
                      <span className="jh-field-label">Create on number</span>
                      <JhSelect value={accountUid} onChange={setAccountUid} searchPlaceholder="Search by name or number"
                        options={accounts.map((a) => ({ value: a.accountUid, label: a.name, description: `${a.phone}${a.isActive ? '' : ' · inactive'}`, keywords: a.phone }))} />
                    </label>
                  )}
                  <Button loading={busy === 'create'} iconLeft={<Sparkles size={14} />} onClick={create}>{both ? 'Resubmit missing / rejected' : 'Create templates'}</Button>
                </div>
              )
            )}
            {allApproved && <p className="flex items-center gap-1.5 text-[12.5px] text-success"><CircleCheck size={14} />Both approved — alerts reach you even when the 24-hour window is closed.</p>}
            {st.account && <p className="jh-muted">Number: {st.account.name} · {st.account.phone}. Meta usually reviews utility templates within minutes to a few hours.</p>}
          </div>
          <div>
            <div className="jh-field-label">Preview — match alert</div>
            <WhatsAppPreview def={st.definitions.alert} />
          </div>
        </div>
      )}
    </section>
  );
}
